/**
 * ExecutiveSummary — "Resumen ejecutivo" de las campañas.
 *
 * Replica el set de gráficas ejecutivas que el equipo comparte por WhatsApp.
 *
 * Mensuales (combo barras+línea / línea):
 *   1) Inversión e impresiones por mes
 *   2) Inversión y clics por mes
 *   3) Inversión vs reproducciones de video por mes
 *   4) Costo por clic (CPC) por mes
 *   5) Costo por mil impresiones (CPM) por mes
 * Por campaña (barras horizontales, Top 10):
 *   6) Impresiones por campaña
 *   7) Clics por campaña
 *   8) Reproducciones de video por campaña
 *
 * No calcula datos nuevos: recibe `trend` (agregado mensual) y `campaigns`
 * (agregado por campaña) que ya arma CampaignsPage, y deriva totales/promedios.
 * Respeta el periodo y la plataforma activos (vía esos props).
 *
 * Expone `getExport()` para que CampaignsPage incruste todas las gráficas
 * (imagen del canvas + tiles) en el PDF.
 */
import React, { useMemo, useRef, forwardRef, useImperativeHandle } from 'react';
import { Bar, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, BarController,
  LineElement, LineController, PointElement, Tooltip, Legend, Filler,
} from 'chart.js';
import {
  FaMousePointer, FaDollarSign, FaEye, FaTag, FaChartBar, FaHandPointer,
  FaCoins, FaArrowDown, FaArrowUp, FaPlay, FaChartLine, FaBullhorn, FaUsers,
  FaBullseye, FaRegClock,
} from 'react-icons/fa';

ChartJS.register(
  CategoryScale, LinearScale, BarElement, BarController, LineElement,
  LineController, PointElement, Tooltip, Legend, Filler,
);

// ── Paleta ────────────────────────────────────────────────────────────────────
const GREEN = '#10b981';
const ORANGE = '#F68D2E';
const ORANGE_DARK = '#d9791f';
const BLUE = '#3b82f6';
const PURPLE = '#8b5cf6';
const PURPLE_DARK = '#7c3aed';
const INDIGO = '#4f46e5';
const CYAN = '#0891b2';
const RED = '#ef4444';

// ── Formateadores ────────────────────────────────────────────────────────────
const NF = new Intl.NumberFormat('es-MX');
const int = (n) => NF.format(Math.round(n || 0));
const money2 = (n) => `$${(n || 0).toFixed(2)}`;
const money3 = (n) => `$${(n || 0).toFixed(3)}`;
const pct = (n, d = 1) => `${((n || 0) * 100).toFixed(d)}%`;

const kmb = (n) => {
  const a = Math.abs(n || 0);
  if (a >= 1e9) return `${(n / 1e9).toFixed(1).replace(/\.0$/, '')} B`;
  if (a >= 1e6) return `${(n / 1e6).toFixed(1).replace(/\.0$/, '')} M`;
  if (a >= 1e3) return `${Math.round(n / 1e3)} k`;
  return `${Math.round(n || 0)}`;
};
const compactTile = (n) => {
  const a = Math.abs(n || 0);
  if (a >= 1e6) return `${(n / 1e6).toFixed(2).replace(/\.?0+$/, '')} M`;
  if (a >= 1e3) return `${(n / 1e3).toFixed(1).replace(/\.0$/, '')} k`;
  return NF.format(Math.round(n || 0));
};
const moneyShort = (n) => {
  const a = Math.abs(n || 0);
  if (a >= 1e6) return `$${(n / 1e6).toFixed(2).replace(/\.?0+$/, '')} M`;
  if (a >= 1e3) return `$${Math.round(n / 1e3)} mil`;
  return `$${Math.round(n || 0)}`;
};
const moneyTile = (n) => `$${compactTile(n)}`;

const ABBR_TO_FULL = {
  Ene: 'Enero', Feb: 'Febrero', Mar: 'Marzo', Abr: 'Abril', May: 'Mayo',
  Jun: 'Junio', Jul: 'Julio', Ago: 'Agosto', Sep: 'Septiembre', Oct: 'Octubre',
  Nov: 'Noviembre', Dic: 'Diciembre',
};
const fullMonth = (abbr) => ABBR_TO_FULL[abbr] || abbr;

// Parte un nombre de campaña largo en máx 2 líneas para el eje.
const wrapLabel = (s, max = 20) => {
  const words = String(s || '').split(/\s+/);
  const lines = [];
  let cur = '';
  for (const w of words) {
    if (cur && (`${cur} ${w}`).length > max) { lines.push(cur); cur = w; }
    else cur = cur ? `${cur} ${w}` : w;
  }
  if (cur) lines.push(cur);
  if (lines.length > 2) { lines[1] = `${lines[1]}…`; lines.length = 2; }
  return lines;
};

// ── Plugin inline de etiquetas de datos ──────────────────────────────────────
// _labelPos: 'insideTop' (barra vertical), 'above' (punto de línea),
// 'endRight' (fin de barra horizontal).
const dataLabels = {
  id: 'execDataLabels',
  afterDatasetsDraw(chart) {
    const { ctx } = chart;
    chart.data.datasets.forEach((ds, di) => {
      if (typeof ds._label !== 'function') return;
      const meta = chart.getDatasetMeta(di);
      if (meta.hidden) return;
      ctx.save();
      ctx.font = ds._labelFont || '700 10px Roboto, system-ui, sans-serif';
      meta.data.forEach((el, i) => {
        const v = ds.data[i];
        if (v == null || !Number.isFinite(v)) return;
        const txt = ds._label(v);
        if (ds._labelPos === 'insideTop') {
          ctx.textAlign = 'center'; ctx.textBaseline = 'top';
          ctx.fillStyle = ds._labelColor || '#fff';
          ctx.fillText(txt, el.x, el.y + 6);
        } else if (ds._labelPos === 'endRight') {
          ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
          ctx.fillStyle = ds._labelColor || '#334155';
          ctx.fillText(txt, el.x + 6, el.y);
        } else {
          ctx.textAlign = 'center'; ctx.textBaseline = 'bottom';
          ctx.fillStyle = ds._labelColor || '#334155';
          ctx.fillText(txt, el.x, el.y - 8);
        }
      });
      ctx.restore();
    });
  },
};

// ── Opciones base ─────────────────────────────────────────────────────────────
const legendCfg = {
  position: 'top', align: 'end',
  labels: { usePointStyle: true, boxWidth: 8, boxHeight: 8, padding: 12, font: { size: 11 } },
};
const xAxis = { grid: { display: false }, ticks: { font: { size: 11 }, color: '#64748b' } };

const moneyAxis = (title, color) => ({
  position: 'left', beginAtZero: true,
  title: { display: true, text: title, color, font: { size: 9, weight: '700' } },
  ticks: { callback: (v) => moneyShort(v), color: '#94a3b8', font: { size: 10 }, maxTicksLimit: 6 },
  grid: { color: '#f1f5f9' },
});
const countAxis = (title, color) => ({
  position: 'right', beginAtZero: true,
  title: { display: true, text: title, color, font: { size: 9, weight: '700' } },
  ticks: { callback: (v) => kmb(v), color: '#94a3b8', font: { size: 10 }, maxTicksLimit: 6 },
  grid: { drawOnChartArea: false },
});

// ── Combo mensual (barras Inversión + línea) ─────────────────────────────────
const comboData = (labels, bars, lineLabel, linePts) => ({
  labels,
  datasets: [
    {
      type: 'bar', label: `Inversión ($ ${CUR})`, data: bars, yAxisID: 'y', order: 2,
      backgroundColor: GREEN, borderRadius: 6, categoryPercentage: 0.6, barPercentage: 0.9,
      _label: moneyShort, _labelPos: 'insideTop', _labelColor: '#fff', _labelFont: '700 9px Roboto, sans-serif',
    },
    {
      // Sin etiquetas sobre la línea: chocaría con las barras. Valor en eje/tooltip.
      type: 'line', label: lineLabel, data: linePts, yAxisID: 'y1', order: 1,
      borderColor: ORANGE, backgroundColor: ORANGE, pointBackgroundColor: ORANGE,
      pointRadius: 3.5, pointHoverRadius: 5, borderWidth: 2.5, tension: 0.35,
    },
  ],
});
const comboOpts = (rightTitle, lineLabel, lineFmt) => ({
  responsive: true, maintainAspectRatio: false, layout: { padding: { top: 10 } },
  plugins: {
    legend: legendCfg,
    tooltip: { callbacks: { label: (c) => (c.datasetIndex === 0 ? `Inversión: ${moneyShort(c.parsed.y)}` : `${lineLabel}: ${lineFmt(c.parsed.y)}`) } },
  },
  scales: { y: moneyAxis(`INVERSIÓN ($ ${CUR})`, GREEN), y1: countAxis(rightTitle, ORANGE), x: xAxis },
});

// ── Línea de costo (CPC / CPM) ────────────────────────────────────────────────
const lineCostData = (labels, label, pts) => ({
  labels,
  datasets: [{
    type: 'line', label, data: pts, spanGaps: true,
    borderColor: ORANGE, backgroundColor: 'rgba(246,141,46,0.07)', fill: true,
    pointBackgroundColor: ORANGE, pointRadius: 4, pointHoverRadius: 6, borderWidth: 2.5, tension: 0.35,
    _label: money2, _labelPos: 'above', _labelColor: ORANGE_DARK, _labelFont: '700 10px Roboto, sans-serif',
  }],
});
const lineCostOpts = (axisTitle) => ({
  responsive: true, maintainAspectRatio: false, layout: { padding: { top: 22 } },
  plugins: {
    legend: legendCfg,
    tooltip: { callbacks: { label: (c) => `${axisTitle}: ${money2(c.parsed.y)}` } },
  },
  scales: {
    y: {
      beginAtZero: true,
      title: { display: true, text: `${axisTitle} (${CUR})`, color: ORANGE, font: { size: 9, weight: '700' } },
      ticks: { callback: (v) => money2(v), color: '#94a3b8', font: { size: 10 }, maxTicksLimit: 6 },
      grid: { color: '#f1f5f9' },
    },
    x: xAxis,
  },
});

// ── Barras horizontales por campaña ───────────────────────────────────────────
// Nombre de campaña para el eje (el tablero de Meta le quita el prefijo común
// de la nomenclatura de la agencia); el completo va en el tooltip.
let CAMP_LABEL = (k) => k;
const perCampaignData = (groups, metric, seriesLabel, barColor, labelColor) => ({
  labels: groups.map((g) => wrapLabel(CAMP_LABEL(g.key))),
  datasets: [{
    fullNames: groups.map((g) => g.key),
    type: 'bar', label: seriesLabel, data: groups.map((g) => Math.round(g[metric] || 0)),
    backgroundColor: barColor, borderRadius: 5, maxBarThickness: 22,
    _label: int, _labelPos: 'endRight', _labelColor: labelColor, _labelFont: '700 10px Roboto, sans-serif',
  }],
});
const perCampaignOpts = (seriesLabel) => ({
  indexAxis: 'y', responsive: true, maintainAspectRatio: false,
  layout: { padding: { right: 74 } },
  plugins: {
    legend: legendCfg,
    tooltip: { callbacks: {
      title: (it) => it[0]?.dataset?.fullNames?.[it[0].dataIndex] || it[0]?.label || '',
      label: (c) => `${seriesLabel}: ${int(c.parsed.x)}`,
    } },
  },
  scales: {
    x: { beginAtZero: true, ticks: { callback: (v) => kmb(v), color: '#94a3b8', font: { size: 10 } }, grid: { color: '#f1f5f9' } },
    y: { ticks: { font: { size: 10.5 }, color: '#334155', autoSkip: false, crossAlign: 'far' }, grid: { display: false } },
  },
});

// ── Componente ────────────────────────────────────────────────────────────────
// Divisa de las etiquetas ("Inversión ($ MXN)"). Es de módulo porque los
// builders de gráficas viven fuera del componente; se fija en cada render.
let CUR = 'MXN';
const CUR_NAME = { MXN: 'Pesos mexicanos (MXN)', USD: 'Dólares (USD)', EUR: 'Euros (EUR)', COP: 'Pesos colombianos (COP)' };

// reachOverride: alcance SIN duplicar (Meta lo da por periodo). La suma de
// alcance mes a mes cuenta varias veces a la misma persona.
function ExecutiveSummary({ trend, campaigns, periodDays, platformLabel, reachOverride = null, currency = 'MXN', campaignLabel = null, periodText = null }, exportRef) {
  CUR = currency || 'MXN';
  CAMP_LABEL = campaignLabel || ((k) => k);
  const refInvImpr = useRef(null);
  const refInvClicks = useRef(null);
  const refInvViews = useRef(null);
  const refCpc = useRef(null);
  const refCpm = useRef(null);
  const refCampImpr = useRef(null);
  const refCampClicks = useRef(null);
  const refCampViews = useRef(null);

  const data = useMemo(() => {
    const months = (trend || []).filter((t) => (t.spend || 0) > 0 || (t.impressions || 0) > 0);
    const labels = months.map((t) => t.label);
    const S = (sel) => months.reduce((a, t) => a + (sel(t) || 0), 0);

    const totalSpend = S((t) => t.spend);
    const totalImpr = S((t) => t.impressions);
    const totalClicks = S((t) => t.clicks);
    const totalViews = S((t) => t.views);
    const totalReach = reachOverride != null ? reachOverride : S((t) => t.reach);

    // Métricas de VIDEO (CPV / VTR / reproducciones por $1,000): solo sobre las
    // plataformas con video. En una vista multi-red (Meta+TikTok+YouTube+Banners),
    // dividir el gasto TOTAL entre vistas de video-only inflaría el CPV y diluiría
    // el VTR. Cada campaña es de una sola plataforma; las de video traen views>0,
    // así que acotamos gasto/impresiones a esas campañas.
    const videoCamps = (campaigns || []).filter((g) => (g.views || 0) > 0);
    const videoSpend = videoCamps.reduce((a, g) => a + (g.spend || 0), 0);
    const videoImpr = videoCamps.reduce((a, g) => a + (g.impressions || 0), 0);

    const cpmEff = totalImpr ? (totalSpend / totalImpr) * 1000 : 0;
    const cpcEff = totalClicks ? totalSpend / totalClicks : 0;
    const cpvEff = totalViews ? videoSpend / totalViews : 0;
    const ctrEff = totalImpr ? totalClicks / totalImpr : 0;
    const vtrEff = videoImpr ? totalViews / videoImpr : 0;
    const imprPer1000 = totalSpend ? (totalImpr / totalSpend) * 1000 : 0;
    const clicksPer1000 = totalSpend ? (totalClicks / totalSpend) * 1000 : 0;
    const viewsPer1000 = videoSpend ? (totalViews / videoSpend) * 1000 : 0;

    const cpcPts = months.map((t) => ((t.clicks || 0) > 0 ? t.cpc : null));
    const cpmPts = months.map((t) => ((t.impressions || 0) > 0 ? t.cpm : null));

    const cpcM = months.filter((t) => (t.clicks || 0) > 0).map((t) => ({ label: t.label, v: t.cpc }));
    const cpmM = months.filter((t) => (t.impressions || 0) > 0).map((t) => ({ label: t.label, v: t.cpm }));
    const mean = (arr) => (arr.length ? arr.reduce((a, x) => a + x.v, 0) / arr.length : 0);
    const minOf = (arr) => (arr.length ? arr.reduce((a, b) => (b.v < a.v ? b : a)) : null);
    const maxOf = (arr) => (arr.length ? arr.reduce((a, b) => (b.v > a.v ? b : a)) : null);

    // Top 10 campañas por cada métrico.
    const camps = campaigns || [];
    const nCampaigns = camps.length;
    const days = periodDays || months.length * 30 || 1;
    const top10 = (metric) => [...camps]
      .filter((g) => (g[metric] || 0) > 0)
      .sort((a, b) => (b[metric] || 0) - (a[metric] || 0))
      .slice(0, 10);

    return {
      labels, months,
      spend: months.map((t) => Math.round(t.spend || 0)),
      impressions: months.map((t) => Math.round(t.impressions || 0)),
      clicks: months.map((t) => Math.round(t.clicks || 0)),
      views: months.map((t) => Math.round(t.views || 0)),
      cpcPts, cpmPts,
      totalSpend, totalImpr, totalClicks, totalViews, totalReach,
      cpmEff, cpcEff, cpvEff, ctrEff, vtrEff,
      imprPer1000, clicksPer1000, viewsPer1000,
      cpcAvg: mean(cpcM), cpmAvg: mean(cpmM),
      cpcMin: minOf(cpcM), cpcMax: maxOf(cpcM),
      cpmMin: minOf(cpmM), cpmMax: maxOf(cpmM),
      nCampaigns, days,
      topImpr: top10('impressions'),
      topClicks: top10('clicks'),
      topViews: top10('views'),
    };
  }, [trend, campaigns, periodDays, reachOverride]);

  // periodText: el periodo tal cual lo nombra el tablero ("Últimos 30 días
  // (26 ago – 24 sep)"). Derivarlo de las etiquetas de mes decía "Agosto a
  // Septiembre" aunque solo fueran unos días de agosto.
  const period = periodText || (data.months.length
    ? `${fullMonth(data.months[0].label)} a ${fullMonth(data.months[data.months.length - 1].label)}`
    : '');
  const note = period
    ? `${periodText ? `Periodo de los datos: ${period}` : `Los datos mostrados corresponden al periodo de ${period}`}${platformLabel && platformLabel !== 'Todas' ? ` · ${platformLabel}` : ''}.`
    : '';
  // Altura de una gráfica de barras horizontales según el nº de campañas.
  const hbarHeight = (n) => `${Math.max(220, n * 34 + 60)}px`;

  // ── Definición única de tarjetas (pantalla + PDF) ──────────────────────────
  const cards = useMemo(() => {
    if (!data.labels.length) return [];
    const monthly = [
      {
        key: 'invImpr', ref: refInvImpr, type: 'bar', group: 'monthly',
        title: 'Inversión e impresiones por mes',
        formula: 'Costo por mil impresiones (CPM) = (Inversión ÷ Impresiones) × 1,000   ·   Impresiones por $1,000 = (Impresiones ÷ Inversión) × 1,000',
        chartData: comboData(data.labels, data.spend, 'Impresiones', data.impressions),
        chartOptions: comboOpts('IMPRESIONES', 'Impresiones', int),
        tiles: [
          { color: GREEN, icon: <FaDollarSign />, label: 'Inversión total', value: moneyTile(data.totalSpend), sub: CUR_NAME[CUR] || CUR },
          { color: ORANGE, icon: <FaEye />, label: 'Impresiones totales', value: compactTile(data.totalImpr), sub: 'Impresiones' },
          { color: BLUE, icon: <FaTag />, label: 'Costo por mil impresiones (CPM)', value: money2(data.cpmEff), sub: 'Costo por cada 1,000 impresiones' },
          { color: PURPLE, icon: <FaChartBar />, label: 'Impresiones por $1,000', value: int(data.imprPer1000), sub: `Impresiones por cada $1,000 ${CUR}` },
        ],
      },
      {
        key: 'invClicks', ref: refInvClicks, type: 'bar', group: 'monthly',
        title: 'Inversión y clics por mes',
        formula: 'Costo por clic (CPC) = Inversión ÷ Clics   ·   Clics por $1,000 = (Clics ÷ Inversión) × 1,000',
        chartData: comboData(data.labels, data.spend, 'Clics', data.clicks),
        chartOptions: comboOpts('CLICS', 'Clics', int),
        tiles: [
          { color: GREEN, icon: <FaDollarSign />, label: 'Inversión total', value: moneyTile(data.totalSpend), sub: CUR_NAME[CUR] || CUR },
          { color: ORANGE, icon: <FaMousePointer />, label: 'Clics totales', value: compactTile(data.totalClicks), sub: 'Clics' },
          { color: BLUE, icon: <FaHandPointer />, label: 'Costo por clic (CPC)', value: money2(data.cpcEff), sub: 'Costo por clic' },
          { color: PURPLE, icon: <FaChartBar />, label: 'Clics por $1,000', value: int(data.clicksPer1000), sub: `Clics por cada $1,000 ${CUR}` },
        ],
      },
      {
        key: 'invViews', ref: refInvViews, type: 'bar', group: 'monthly',
        title: 'Inversión vs reproducciones de video por mes',
        formula: 'Costo por reproducción (CPV) = Inversión ÷ Vistas   ·   Tasa de reproducción (VTR) = Vistas ÷ Impresiones   ·   Reproducciones por $1,000 = (Vistas ÷ Inversión) × 1,000',
        chartData: comboData(data.labels, data.spend, 'Reproducciones', data.views),
        chartOptions: comboOpts('REPRODUCCIONES', 'Reproducciones', int),
        tiles: [
          { color: ORANGE, icon: <FaPlay />, label: 'Reproducciones de video', value: compactTile(data.totalViews), sub: 'Vistas de video' },
          { color: BLUE, icon: <FaCoins />, label: 'Costo por reproducción (CPV)', value: money3(data.cpvEff), sub: 'Costo por vista' },
          { color: PURPLE, icon: <FaChartLine />, label: 'Tasa de reproducción (VTR)', value: pct(data.vtrEff, 1), sub: 'Vistas sobre impresiones' },
          { color: GREEN, icon: <FaDollarSign />, label: 'Inversión total', value: moneyTile(data.totalSpend), sub: CUR },
          { color: INDIGO, icon: <FaChartBar />, label: 'Reproducciones por $1,000', value: int(data.viewsPer1000), sub: `Vistas por cada $1,000 ${CUR}` },
        ],
      },
      {
        key: 'cpc', ref: refCpc, type: 'line', group: 'monthly',
        title: 'Costo por clic (CPC) por mes',
        formula: 'Costo por clic (mes) = Inversión del mes ÷ Clics del mes   ·   Promedio = media de los costos por clic mensuales',
        chartData: lineCostData(data.labels, `Costo por clic (CPC) — ${CUR}`, data.cpcPts),
        chartOptions: lineCostOpts('Costo por clic'),
        tiles: [
          { color: GREEN, icon: <FaCoins />, label: 'Costo por clic (CPC)', value: money2(data.cpcAvg), sub: 'Media mensual' },
          { color: ORANGE, icon: <FaMousePointer />, label: 'Clics totales', value: compactTile(data.totalClicks), sub: 'Clics' },
          { color: BLUE, icon: <FaDollarSign />, label: 'Inversión total', value: moneyTile(data.totalSpend), sub: CUR },
          ...(data.cpcMin ? [{ color: GREEN, icon: <FaArrowDown />, label: 'Costo por clic (CPC) más bajo', value: money2(data.cpcMin.v), sub: fullMonth(data.cpcMin.label) }] : []),
          ...(data.cpcMax ? [{ color: RED, icon: <FaArrowUp />, label: 'Costo por clic (CPC) más alto', value: money2(data.cpcMax.v), sub: fullMonth(data.cpcMax.label) }] : []),
        ],
      },
      {
        key: 'cpm', ref: refCpm, type: 'line', group: 'monthly',
        title: 'Costo por mil impresiones (CPM) por mes',
        formula: 'Costo por mil impresiones (mes) = (Inversión del mes ÷ Impresiones del mes) × 1,000   ·   Promedio = media de los costos por mil impresiones mensuales',
        chartData: lineCostData(data.labels, `Costo por mil impresiones (CPM) — ${CUR}`, data.cpmPts),
        chartOptions: lineCostOpts('Costo por mil impresiones'),
        tiles: [
          { color: GREEN, icon: <FaCoins />, label: 'Costo por mil impresiones (CPM)', value: money2(data.cpmAvg), sub: 'Media mensual' },
          { color: ORANGE, icon: <FaEye />, label: 'Impresiones totales', value: compactTile(data.totalImpr), sub: 'Impresiones' },
          { color: BLUE, icon: <FaDollarSign />, label: 'Inversión total', value: moneyTile(data.totalSpend), sub: CUR },
          ...(data.cpmMin ? [{ color: GREEN, icon: <FaArrowDown />, label: 'Costo por mil impresiones (CPM) más bajo', value: money2(data.cpmMin.v), sub: fullMonth(data.cpmMin.label) }] : []),
          ...(data.cpmMax ? [{ color: RED, icon: <FaArrowUp />, label: 'Costo por mil impresiones (CPM) más alto', value: money2(data.cpmMax.v), sub: fullMonth(data.cpmMax.label) }] : []),
        ],
      },
    ];

    const dailyImpr = data.days ? data.totalImpr / data.days : 0;
    const dailyClicks = data.days ? data.totalClicks / data.days : 0;
    const viewsPerCamp = data.nCampaigns ? data.totalViews / data.nCampaigns : 0;

    const campaign = [
      {
        key: 'campImpr', ref: refCampImpr, type: 'bar', group: 'campaign', wide: true,
        title: 'Impresiones por campaña', chartHeight: hbarHeight(data.topImpr.length),
        formula: 'Promedio diario = Impresiones ÷ días del periodo   ·   Costo por mil impresiones (CPM) = (Inversión ÷ Impresiones) × 1,000',
        chartData: perCampaignData(data.topImpr, 'impressions', 'Impresiones', PURPLE, PURPLE_DARK),
        chartOptions: perCampaignOpts('Impresiones'),
        tiles: [
          { color: PURPLE, icon: <FaEye />, label: 'Impresiones totales', value: compactTile(data.totalImpr), sub: 'Total de impresiones' },
          { color: GREEN, icon: <FaRegClock />, label: 'Promedio diario', value: int(dailyImpr), sub: 'Impresiones por día' },
          { color: ORANGE, icon: <FaUsers />, label: 'Alcance total', value: compactTile(data.totalReach), sub: 'Usuarios alcanzados' },
          { color: BLUE, icon: <FaTag />, label: 'Costo por mil impresiones (CPM)', value: money2(data.cpmEff), sub: 'Costo por mil impresiones' },
          { color: RED, icon: <FaBullseye />, label: 'Campañas activas', value: int(data.nCampaigns), sub: 'En el periodo' },
        ],
      },
      {
        key: 'campClicks', ref: refCampClicks, type: 'bar', group: 'campaign', wide: true,
        title: 'Clics por campaña', chartHeight: hbarHeight(data.topClicks.length),
        formula: 'Tasa de clics (CTR) = Clics ÷ Impresiones   ·   Promedio diario = Clics ÷ días del periodo',
        chartData: perCampaignData(data.topClicks, 'clicks', 'Clics', BLUE, '#1d4ed8'),
        chartOptions: perCampaignOpts('Clics'),
        tiles: [
          { color: BLUE, icon: <FaMousePointer />, label: 'Clics totales', value: compactTile(data.totalClicks), sub: 'Total de clics' },
          { color: GREEN, icon: <FaRegClock />, label: 'Promedio diario', value: int(dailyClicks), sub: 'Clics por día' },
          { color: PURPLE, icon: <FaChartLine />, label: 'Tasa de clics (CTR)', value: pct(data.ctrEff, 2), sub: 'Clics sobre impresiones' },
          { color: ORANGE, icon: <FaUsers />, label: 'Alcance', value: compactTile(data.totalReach), sub: 'Usuarios alcanzados' },
          { color: RED, icon: <FaBullseye />, label: 'Campañas activas', value: int(data.nCampaigns), sub: 'En el periodo' },
        ],
      },
      {
        key: 'campViews', ref: refCampViews, type: 'bar', group: 'campaign', wide: true, fullWidth: true,
        title: 'Reproducciones de video por campaña', chartHeight: hbarHeight(data.topViews.length),
        formula: 'Tasa de reproducción (VTR) = Vistas ÷ Impresiones   ·   Promedio por campaña = Vistas ÷ nº de campañas',
        chartData: perCampaignData(data.topViews, 'views', 'Reproducciones de video', CYAN, '#0e7490'),
        chartOptions: perCampaignOpts('Reproducciones de video'),
        tiles: [
          { color: CYAN, icon: <FaPlay />, label: 'Reproducciones totales', value: compactTile(data.totalViews), sub: 'Total de reproducciones' },
          { color: GREEN, icon: <FaChartBar />, label: 'Promedio por campaña', value: compactTile(viewsPerCamp), sub: 'Vistas / campaña' },
          { color: PURPLE, icon: <FaChartLine />, label: 'Tasa de reproducción (VTR)', value: pct(data.vtrEff, 1), sub: 'Vistas sobre impresiones' },
          { color: ORANGE, icon: <FaUsers />, label: 'Alcance', value: compactTile(data.totalReach), sub: 'Usuarios alcanzados' },
          { color: RED, icon: <FaBullseye />, label: 'Campañas activas', value: int(data.nCampaigns), sub: 'En el periodo' },
        ],
      },
    ].filter((c) => c.chartData.labels.length); // omite si no hay campañas con ese métrico

    return [...monthly, ...campaign];
  }, [data]);

  useImperativeHandle(exportRef, () => ({
    getExport: () => {
      const grab = (ref) => { try { return ref.current?.toBase64Image('image/png', 1) || ''; } catch (e) { return ''; } };
      return {
        period, note,
        cards: cards.map((c) => ({
          title: c.title, wide: !!c.wide, img: grab(c.ref), formula: c.formula || '',
          tiles: c.tiles.map((t) => ({ color: t.color, label: t.label, value: t.value, sub: t.sub })),
        })),
      };
    },
  }));

  if (!cards.length) return null;
  const monthlyCards = cards.filter((c) => c.group === 'monthly');
  const campaignCards = cards.filter((c) => c.group === 'campaign');

  const renderCard = (c) => (
    <ExecCard key={c.key} title={c.title} className={c.fullWidth ? 'exec-card-full' : ''}>
      <div className="exec-chart" style={c.chartHeight ? { height: c.chartHeight } : undefined}>
        {c.type === 'line'
          ? <Line ref={c.ref} data={c.chartData} options={c.chartOptions} plugins={[dataLabels]} />
          : <Bar ref={c.ref} data={c.chartData} options={c.chartOptions} plugins={[dataLabels]} />}
      </div>
      <div className="exec-tiles">
        {c.tiles.map((t, i) => <ExecTile key={i} {...t} />)}
      </div>
      {note && <p className="exec-note">{note}</p>}
      {c.formula && <p className="exec-formula"><span className="exec-formula-tag">ƒ</span> {c.formula}</p>}
    </ExecCard>
  );

  return (
    <section className="exec-section">
      <div className="exec-head">
        <span className="exec-head-title"><FaChartBar /> Resumen ejecutivo</span>
        <span className="exec-head-tag">{period ? `Periodo · ${period}` : 'Resumen'}</span>
      </div>
      <div className="exec-grid">{monthlyCards.map(renderCard)}</div>

      {campaignCards.length > 0 && (
        <>
          <div className="exec-head exec-head-sub">
            <span className="exec-head-title"><FaBullhorn /> Por campaña</span>
            <span className="exec-head-tag">Top 10{period ? ` · ${period}` : ''}</span>
          </div>
          <div className="exec-grid exec-grid-wide">{campaignCards.map(renderCard)}</div>
        </>
      )}
    </section>
  );
}

export default forwardRef(ExecutiveSummary);

// ── Subcomponentes ─────────────────────────────────────────────────────────────
function ExecCard({ title, className, children }) {
  return (
    <section className={`exec-card${className ? ` ${className}` : ''}`}>
      <div className="exec-card-head">
        <span className="exec-card-title"><FaMousePointer /> {title}</span>
      </div>
      {children}
    </section>
  );
}

function ExecTile({ color, icon, label, value, sub }) {
  return (
    <div className="exec-tile" style={{ '--tile': color }}>
      <span className="exec-tile-icon">{icon}</span>
      <div className="exec-tile-body">
        <span className="exec-tile-label">{label}</span>
        <span className="exec-tile-value">{value}</span>
        <span className="exec-tile-sub">{sub}</span>
      </div>
    </div>
  );
}
